import { JSX, splitProps, ParentComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import styles from "./Text.module.css";

/**
 * Text Variants
 *
 * Different text styles used throughout the application
 */
export type TextVariant =
  // Body text
  | "body" // Default body text (16px)
  | "bodyLarge" // Larger body text (18px)
  | "bodySmall" // Smaller body text (14px)
  | "bodyWhite" // Body text with white color (for dark backgrounds)

  // Specialized text
  | "description" // Content descriptions
  | "heroDescription" // Hero section descriptions
  | "caption" // Image captions, credits
  | "label" // Form labels, badges
  | "error" // Error messages
  | "textButtonLink" // Text button links
  | "link" // Inline link styling
  | "overline" // Small labels above content
  | "heroOverline" // Larger overline for hero sections
  | "heroEmojis" // Large emoji text for hero sections
  | "documentDescription" // Document download body text (18px/32px, dark)
  | "teaserSenderTitle"

  // Badge/tag text
  | "badge" // Badge text (uppercase, bold)
  | "tag" // Tag/category labels

  // Credits
  | "imageCredit" // Image credit text
  | "authorCredit"; // Author credit text

/**
 * Props for the Text component
 */
export interface TextProps {
  /**
   * Visual variant controlling the styling
   * @default "body"
   */
  variant?: TextVariant;

  /**
   * HTML element to render
   * @default "p" for most variants, "span" for inline variants
   */
  as?: "p" | "span" | "div" | "label";

  /**
   * Center align the text
   */
  center?: boolean;

  /**
   * Add custom CSS classes
   */
  class?: string;

  /**
   * classList for conditional classes
   */
  classList?: Record<string, boolean>;

  /**
   * Content to render
   */
  children?: JSX.Element;
}

/**
 * Map variants to their default HTML elements
 */
const variantToElementMap: Record<TextVariant, "p" | "span" | "label"> = {
  // Block elements
  body: "p",
  bodyLarge: "p",
  bodySmall: "p",
  bodyWhite: "p",
  description: "p",
  heroDescription: "p",
  error: "p",
  imageCredit: "p",
  authorCredit: "p",
  heroEmojis: "p",
  textButtonLink: "span",
  teaserSenderTitle: "p",

  // Inline elements
  link: "span",
  caption: "span",
  label: "label",
  badge: "span",
  tag: "span",
  overline: "span",
  heroOverline: "span",
  documentDescription: "p",
};

/**
 * Text Component
 *
 * A flexible text component for rendering various text styles
 * throughout the application.
 *
 * @example
 * ```tsx
 * <Text>Default body text</Text>
 * <Text variant="bodyLarge">Larger text</Text>
 * <Text variant="caption">Small caption</Text>
 * <Text variant="badge">BADGE TEXT</Text>
 * ```
 */
export const Text: ParentComponent<TextProps> = (props) => {
  const [local, others] = splitProps(props, [
    "variant",
    "as",
    "center",
    "class",
    "classList",
    "children",
  ]);

  const variant = () => local.variant || "body";
  const element = () => local.as || variantToElementMap[variant()];

  return (
    <Dynamic
      component={element()}
      class={styles.text}
      classList={{
        [styles.center]: local.center,
        [styles[variant()]]: true,
        [local.class || ""]: !!local.class,
        ...local.classList,
      }}
      {...others}
    >
      {local.children}
    </Dynamic>
  );
};

export default Text;
